import { useState, useEffect, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import MobileLayout from "../../components/General/MobileLayout/MobileLayout";
import BottomNavBar from "../../components/BottomNavBar";
import { message } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import activityService from "../../services/ActivityService";

// Import all activity icons - Using SVG versions where available
import SleepTrackingIcon from "../../assets/sleeptrakking.svg";
import tootleflooors from "../../assets/tootleflooors.svg";
import RunningIcon from "../../assets/RunningIcon.svg";
import OxygenIcon from "../../assets/oxygen.svg";
import BreathingIcon from "../../assets/breathingrate.svg";
import SkinTemperatureIcon from "../../assets/Grouskintemperature.svg";
import HeartRateIcon from "../../assets/recoveryrhythm.svg";
import StressIcon from "../../assets/stressicon.svg";
import CaloriesBurnedIcon from "../../assets/calories 1.svg";
import DistanceIcon from "../../assets/distancecovered.svg";
import ClockIcon from "../../assets/activityClock.svg";
import CalendarScrollIcon from "../../assets/CalendarScrollIcon.svg";
import ManIcon from "../../assets/man.svg";
import BellIcon from "../../assets/bell.svg";
import bodyfat from "../../assets/bodyfat.svg";
import WeightIcon from "../../assets/weight.svg";
import BMIIcon from "../../assets/bmi.svg";
import ActivityZoneIcon from "../../assets/activityzone.svg";

const ActivityPage = () => {
  const [currentDay, setCurrentDay] = useState(2); // Start with Today (index 2)
  const [error, setError] = useState<string | null>(null);

  // Generate dates: Day Before Yesterday, Yesterday, Today (reverse order for left swipe)
  const dates = useMemo(() => {
    const dateList = [];
    const today = new Date();

    // Day Before Yesterday (first - index 0, leftmost)
    const dayBeforeYesterday = new Date(today);
    dayBeforeYesterday.setDate(today.getDate() - 2);
    dateList.push(dayBeforeYesterday);

    // Yesterday (second - index 1, middle)
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    dateList.push(yesterday);

    // Today (third - index 2, rightmost, default)
    dateList.push(new Date(today));

    return dateList;
  }, []); // Empty dependency array since dates should only be generated once per day

  // Create stable date strings for query key
  const dateStrings = useMemo(
    () => dates.map(date => activityService.formatDateForAPI(date)),
    [dates]
  );

  // Fetch activity data with React Query
  const {
    data: activityData = [],
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ["activityData", dateStrings], // Use stable strings instead of Date objects
    queryFn: async () => {
      const promises = dates.map(async date => {
        try {
          const dateStr = activityService.formatDateForAPI(date);
          const apiData = await activityService.getActivityData(dateStr);
          const transformedData = activityService.transformActivityData(
            apiData as any
          );

          return {
            ...transformedData,
            date: activityService.formatDateForDisplay(date),
            rawData: apiData,
          };
        } catch (_err) {
          // Return placeholder data if API fails
          return {
            date: activityService.formatDateForDisplay(date),
            steps: "0",
            target: "10,000",
            percentage: "0%",
            activityTime: "0 min",
            heartRate: "0",
            calories: "0",
            distance: "0.0",
            sleepHours: "0.0",
            floors: "0",
            oxygenSaturation: "0%",
            breathingRate: "0 bpm",
            skinTemp: "0°C",
            weight: "0 kg",
            bodyFat: "0%",
            bmi: "0",
            hrv: "0 ms",
            stress: "Normal",
            rawData: null,
          };
        }
      });

      return await Promise.all(promises);
    },
  });

  // Get current activity data
  const currentActivityData = activityData[currentDay] ?? {
    date: "Loading...",
    steps: "0",
    target: "10,000",
    percentage: "0%",
    activityTime: "0 min",
    heartRate: "0",
    calories: "0",
    distance: "0.0",
    sleepHours: "0.0",
    floors: "0",
    oxygenSaturation: "0%",
    breathingRate: "0 bpm",
    skinTemp: "0°C",
    weight: "0 kg",
    bodyFat: "0%",
    bmi: "0",
    hrv: "0 ms",
    stress: "Normal",
  };

  // Get date label (Today, Yesterday, Day Before Yesterday)
  const getDateLabel = (dayIndex: number): string => {
    if (dayIndex === 2) {
      return "Today";
    }
    if (dayIndex === 1) {
      return "Yesterday";
    }
    return "Day Before Yesterday";
  };

  const currentDateLabel = getDateLabel(currentDay);

  // Error state
  if (error && activityData.length === 0) {
    return (
      <MobileLayout>
        <div className="flex min-h-screen items-center justify-center bg-[#171916] px-4">
          <div className="text-center">
            <div className="mb-4 text-6xl">⚠️</div>
            <h2 className="mb-2 text-title2 font-bold text-white">
              Failed to Load Data
            </h2>
            <p className="mb-4 text-gray-400">{error}</p>
            <button
              onClick={() => refetch()}
              className="rounded-lg bg-[#C6F68D] px-4 py-2 font-semibold text-black"
            >
              Try Again
            </button>
          </div>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout>
      <div className="scrollbar-hide mx-auto min-h-screen w-full max-w-[400px] overflow-auto bg-[#171916] pb-16">
        {/* Header */}
        <div className="flex items-center justify-between px-4 pb-2 pt-4">
          <div className="flex items-center gap-3">
            <LeftOutlined
              className="cursor-pointer text-title2 text-white"
              onClick={() => window.history.back()}
            />
            <h1 className="text-title1 font-bold text-white">
              Today&apos;s Activities
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button className="flex h-10 w-10 items-center justify-center rounded-full bg-[#414141] p-2">
              <img
                src={BellIcon}
                alt="Notifications"
                className="h-5 w-5 object-contain"
              />
            </button>
          </div>
        </div>

        {/* Loading Overlay with Blur Background */}
        {loading && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
            }}
          >
            <div className="flex flex-col items-center justify-center text-center">
              <div className="mb-4 h-16 w-16 animate-spin rounded-full border-4 border-gray-600 border-t-[#C6F68D]" />
              <p className="text-title3 font-semibold text-white">
                Loading activity data...
              </p>
              <p className="mt-2 text-subhead text-gray-300">
                Please wait while we fetch your data
              </p>
            </div>
          </div>
        )}

        {/* Today&apos;s Activity Section */}
        <div className="mx-4 mt-2">
          <div className="mb-4 rounded-xl bg-[#2F312E] p-4">
            <div className="mb-4 flex items-center justify-between">
              <LeftOutlined
                className="cursor-pointer text-title3 text-white opacity-60"
                onClick={() =>
                  setCurrentDay(
                    currentDay > 0 ? currentDay - 1 : activityData.length - 1
                  )
                }
              />
              <div className="flex flex-col items-center gap-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-white">
                    {currentActivityData.date}
                  </span>
                  <img
                    src={CalendarScrollIcon}
                    alt="Calendar"
                    className="h-4 w-4"
                  />
                </div>
                {/* Today/Yesterday/Day Before Yesterday Label */}
                <span
                  className={`text-caption1 font-medium ${
                    currentDay === 2 ? "text-[#C6F68D]" : "text-gray-400"
                  }`}
                >
                  {currentDateLabel}
                </span>
              </div>
              {/* Only show right arrow if not on Today */}
              {currentDay < 2 ? (
                <RightOutlined
                  className="cursor-pointer text-title3 text-white opacity-60"
                  onClick={() => setCurrentDay(currentDay + 1)}
                />
              ) : (
                <div className="w-5" />
              )}
            </div>

            {/* Steps Section with Image */}
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="mb-2 flex items-center gap-2">
                  <div
                    className="rounded-full bg-[#414141] px-3 py-2"
                    style={{ borderRadius: "50px" }}
                  >
                    <img src={RunningIcon} alt="Steps" className="h-4 w-4" />
                  </div>
                  <span className="text-subhead text-gray-400">
                    Today&apos;s Steps
                  </span>
                </div>
                <div className="mb-1">
                  <span className="text-4xl font-bold text-white">
                    {currentActivityData.steps}
                  </span>
                  <span className="ml-2 text-[#C6F68D]">
                    /{currentActivityData.target}
                  </span>
                </div>
                <p className="mb-2 text-caption1 text-white">
                  Your {currentActivityData.percentage} towards your daily goal.
                  <br />
                  Just Keep moving!
                </p>
                <div className="inline-flex items-center gap-1 rounded-full bg-[#C6F68D] px-3 py-1 text-caption1 font-semibold text-black">
                  <img src={ClockIcon} alt="Clock" className="h-3 w-3" />
                  Activity Time: {currentActivityData.activityTime}
                </div>
              </div>
              <div className="ml-4">
                <img
                  src={ManIcon}
                  alt="Man"
                  className="h-32 w-24 rounded-lg object-cover"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Heart Rate Card */}
        <div className="mx-4 mt-4 rounded-xl bg-[#2F312E] p-4">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-title3 font-semibold text-white">
                Heart_rate
              </span>
              <div className="mt-2">
                <span className="text-4xl font-bold text-white">
                  {currentActivityData.heartRate}
                </span>
                <span className="ml-2 text-gray-400">BPM</span>
              </div>
            </div>
            <div className="rounded-lg bg-[#414141] p-3">
              <img src={HeartRateIcon} alt="Heart Rate" className="h-6 w-6" />
            </div>
          </div>
        </div>

        {/* Stats Grid - Calories & Distance */}
        <div className="mx-4 mb-2 mt-4 grid grid-cols-2 gap-3">
          {/* Calories Burned */}
          <div className="rounded-xl bg-[#2F312E] p-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-subhead font-bold text-white">
                Calories Burned
              </span>
              <div className="rounded-lg bg-[#414141] p-3">
                <img
                  src={CaloriesBurnedIcon}
                  alt="Calories"
                  className="h-6 w-6"
                />
              </div>
            </div>
            <div>
              <span className="text-large-title font-bold text-white">
                {currentActivityData.calories}
              </span>
            </div>
            <span className="text-subhead text-white">kcal</span>
          </div>

          {/* Distance Covered */}
          <div className="rounded-xl bg-[#2F312E] p-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-subhead font-bold text-white">
                Distance Covered
              </span>
              <div className="rounded-lg bg-[#414141] p-3">
                <img src={DistanceIcon} alt="Distance" className="h-6 w-6" />
              </div>
            </div>
            <div>
              <span className="text-large-title font-bold text-white">
                {currentActivityData.distance}
              </span>
            </div>
            <span className="text-subhead text-white">km</span>
          </div>
        </div>

        {/* Health Stats Cards - Original Content */}
        <div className="space-y-3 px-4 pt-2">
          {/* Sleep Tracking & Total Floors */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-[#2F2F2F] p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-subhead font-bold text-white">
                  Sleep Tracking
                </span>
                <div className="rounded-lg bg-[#414141] p-3">
                  <img
                    src={SleepTrackingIcon}
                    alt="Sleep"
                    className="h-6 w-6"
                  />
                </div>
              </div>
              <div className="mb-1 text-large-title font-bold text-white">
                {currentActivityData.sleepHours}
              </div>
              <div className="text-subhead text-white">hrs</div>
            </div>

            <div className="rounded-2xl bg-[#2F2F2F] p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-subhead font-bold text-white">
                  Total Floors
                </span>
                <div className="rounded-lg bg-[#414141] p-3">
                  <img
                    src={tootleflooors}
                    alt="Total Floors"
                    className="h-6 w-6"
                  />
                </div>
              </div>
              <div className="mb-1 text-large-title font-bold text-white">
                {currentActivityData.floors}
              </div>
              <div className="text-subhead text-white">Floor</div>
            </div>
          </div>

          {/* Active Zone Minutes */}
          <div className="rounded-2xl bg-[#2F2F2F] p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-subhead font-bold text-white">
                Active Zone Minutes
              </span>
              <div className="rounded-lg bg-[#414141] p-3">
                <img
                  src={ActivityZoneIcon}
                  alt="Activity Zone"
                  className="h-6 w-6"
                />
              </div>
            </div>
            <div className="flex items-baseline">
              <span className="text-4xl font-bold text-white">
                {currentActivityData.activityTime.replace(" min", "")}
              </span>
              <span className="ml-2 text-subhead text-white">min</span>
            </div>
          </div>

          {/* Oxygen Saturation */}
          <div className="rounded-2xl bg-[#2F2F2F] p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-subhead font-bold text-white">
                Oxygen Saturation (SPO2)
              </span>
              <div className="rounded-lg bg-[#414141] p-3">
                <img src={OxygenIcon} alt="Oxygen" className="h-6 w-6" />
              </div>
            </div>
            <div className="flex items-baseline">
              <span className="text-4xl font-bold text-white">
                {currentActivityData.oxygenSaturation.replace("%", "")}
              </span>
              <span className="ml-2 text-subhead text-white">SpO2</span>
            </div>
          </div>

          {/* Breathing Rate */}
          <div className="rounded-2xl bg-[#2F2F2F] p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-subhead font-bold text-white">
                Breathing Rate
              </span>
              <div className="rounded-lg bg-[#414141] p-3">
                <img src={BreathingIcon} alt="Breathing" className="h-6 w-6" />
              </div>
            </div>
            <div className="flex items-baseline">
              <span className="text-4xl font-bold text-white">
                {currentActivityData.breathingRate.replace(" bpm", "")}
              </span>
              <span className="ml-2 text-subhead text-white">bpm</span>
            </div>
          </div>

          {/* Skin Temperature Variation */}
          <div className="rounded-2xl bg-[#2F2F2F] p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-subhead font-bold text-white">
                Skin Temperature Variation
              </span>
              <div className="rounded-lg bg-[#414141] p-3">
                <img
                  src={SkinTemperatureIcon}
                  alt="Skin Temperature"
                  className="h-6 w-6"
                />
              </div>
            </div>
            <div className="flex items-baseline">
              <span className="text-4xl font-bold text-white">
                {currentActivityData.skinTemp.replace("°C", "")}
              </span>
              <span className="ml-2 text-subhead text-white">°C</span>
            </div>
          </div>

          {/* Weight & Body Fat */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-[#2F2F2F] p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-subhead font-bold text-white">Weight</span>
                <div className="rounded-lg bg-[#414141] p-3">
                  <img src={WeightIcon} alt="Weight" className="h-6 w-6" />
                </div>
              </div>
              <div className="mb-1 text-large-title font-bold text-white">
                {currentActivityData.weight.replace(" kg", "")}
              </div>
              <div className="text-subhead text-white">kg</div>
            </div>

            <div className="rounded-2xl bg-[#2F2F2F] p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-subhead font-bold text-white">Body Fat</span>
                <div className="rounded-lg bg-[#414141] p-3">
                  <img src={bodyfat} alt="Body Fat" className="h-6 w-6" />
                </div>
              </div>
              <div className="mb-1 text-large-title font-bold text-white">
                {currentActivityData.bodyFat.replace("%", "")}
              </div>
              <div className="text-subhead text-white">%</div>
            </div>
          </div>

          {/* BMI */}
          <div className="rounded-2xl bg-[#2F2F2F] p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-subhead font-bold text-white">BMI</span>
              <div className="rounded-lg bg-[#414141] p-3">
                <img src={BMIIcon} alt="BMI" className="h-6 w-6" />
              </div>
            </div>
            <div className="text-4xl font-bold text-white">
              {currentActivityData.bmi}
            </div>
          </div>

          {/* Heart Rate Variability */}
          <div className="rounded-2xl bg-[#2F2F2F] p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-subhead font-bold text-white">
                Heart Rate Variability (HRV)
              </span>
              <div className="rounded-lg bg-[#414141] p-3">
                <img
                  src={HeartRateIcon}
                  alt="Heart Rate Variability"
                  className="h-6 w-6"
                />
              </div>
            </div>
            <div className="flex items-baseline">
              <span className="text-4xl font-bold text-white">
                {currentActivityData.hrv.replace(" ms", "")}
              </span>
              <span className="ml-2 text-subhead text-white">ms</span>
            </div>
          </div>

          {/* Stress */}
          <div className="rounded-2xl bg-[#2F2F2F] p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-subhead font-bold text-white">Stress</span>
              <div className="rounded-lg bg-[#414141] p-3">
                <img src={StressIcon} alt="Stress" className="h-6 w-6" />
              </div>
            </div>
            <div className="text-title1 font-bold text-white">
              {currentActivityData.stress}
            </div>
          </div>
        </div>

        {/* Bottom Navigation Bar */}
        <BottomNavBar activeIndex={2} />
      </div>
    </MobileLayout>
  );
};

export default ActivityPage;
